import { z } from "zod";
import { LEAD_SORT_FIELDS } from "./leads.js";
import { actorViewSchema } from "./people.js";
import { hasControlCharacter, hasLoneSurrogate } from "./text.js";
// Viste delle tabelle (piano M3, Task 5; D5; `CRM_DATA_DICTIONARY.md` §8.3–8.6): per ogni contesto (l'elenco Lead, o una
// Smart List) la vista dell'azienda, versionata, e la vista personale di ogni appartenenza. La precedenza è fissa e la
// risolve l'interfaccia: vista iniziale del prodotto → vista dell'azienda → vista personale → disposizione non salvata
// sul dispositivo. Una vista non concede nulla: è l'ordine, la visibilità e la larghezza di colonne che il server dà
// già con le righe, e per l'elenco Lead l'ordinamento (LEAD-17); in una Smart List l'ordine è quello della lista, che si
// salva con «Salva» (SL-8), e la vista non ne porta uno. Un `fieldId` che il catalogo delle colonne non ha più si scarta quando si legge e non rompe la
// vista; il server conserva le colonne come sono state salvate.
/** I contesti di una vista: l'elenco Lead (`scopeId` vuoto) e una Smart List (`scopeId` = id della lista). */
export const TABLE_SCOPE_TYPES = ["leads", "smart_list"];
export const COLUMN_WIDTH_MIN = 60;
export const COLUMN_WIDTH_MAX = 800;
/**
 * Una colonna: il suo identificativo stabile (`lead`, `city`, … , `field:<chiave>`), se si vede e la larghezza in
 * pixel, se è stata scelta. L'identificativo finisce in una colonna jsonb, che rifiuta un NUL e una metà sola di una
 * coppia di surrogati con un errore (un `500`): si fermano qui, come negli altri testi (`text.ts`).
 */
export const tableColumnSchema = z.strictObject({
    fieldId: z
        .string()
        .min(1)
        .max(80)
        .refine((value) => !hasControlCharacter(value) && !hasLoneSurrogate(value), { message: "Control characters and unpaired surrogates are not allowed" }),
    visible: z.boolean(),
    width: z.number().int().min(COLUMN_WIDTH_MIN).max(COLUMN_WIDTH_MAX).optional(),
});
/** Da una a ottanta colonne, ognuna una volta sola. */
export const tableColumnsSchema = z
    .array(tableColumnSchema)
    .min(1)
    .max(80)
    .refine((columns) => new Set(columns.map((column) => column.fieldId)).size === columns.length, { message: "A column appears more than once" });
/**
 * L'ordinamento di una vista dell'elenco Lead (LEAD-17): la colonna e il verso, come `sort` e `direction` dell'elenco.
 * `null` è una vista senza ordinamento, che lascia quello di partenza (data di creazione, dal più recente).
 */
export const tableSortSchema = z.strictObject({ field: z.enum(LEAD_SORT_FIELDS), direction: z.enum(["asc", "desc"]) });
/**
 * `GET /api/table-layouts/leads` (e, dal Task 7, quella di una Smart List): l'ultima vista dell'azienda del contesto,
 * se c'è, con chi l'ha pubblicata; la vista personale di chi guarda, se c'è (mai per il gestore della piattaforma, che
 * non ha un'appartenenza), con la versione della vista dell'azienda da cui è partita; se chi guarda può pubblicare. Il
 * server dà sempre `sort` (`null` senza ordinamento); assente vale `null`.
 * `PUT …/mine` risponde lo stesso, dopo il salvataggio.
 */
export const tableLayoutResponseSchema = z.object({
    company: z
        .object({ version: z.number().int(), columns: z.array(tableColumnSchema), sort: tableSortSchema.nullish(), publishedAt: z.string(), publishedBy: actorViewSchema })
        .nullable(),
    personal: z.object({ columns: z.array(tableColumnSchema), sort: tableSortSchema.nullish(), baseVersion: z.number().int().nullable(), updatedAt: z.string() }).nullable(),
    canPublish: z.boolean(),
});
/**
 * Corpo di `PUT …/mine`, «Salva per me»: la vista personale di chi guarda per quel contesto, intera, con l'ordinamento
 * (assente o `null`: nessuno). In una Smart List un ordinamento è `422` sul campo `sort`.
 */
export const saveTableLayoutBodySchema = z.strictObject({ columns: tableColumnsSchema, sort: tableSortSchema.nullable().optional() });
/**
 * Corpo di `POST …/publish`, «Salva per tutta {organization}»: la vista intera e la versione da cui parte (0 = nessuna
 * vista pubblicata). Se non è l'ultima del contesto, `409 version_conflict` con `details.version`.
 */
export const publishTableLayoutBodySchema = z.strictObject({ columns: tableColumnsSchema, sort: tableSortSchema.nullable().optional(), expectedVersion: z.number().int().min(0) });
/** La versione pubblicata (quella attuale, se non cambia nulla) e quante viste personali del contesto si sono azzerate. */
export const publishTableLayoutResponseSchema = z.object({ version: z.number().int(), invalidatedOverrides: z.number().int() });
//# sourceMappingURL=table-layouts.js.map